import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import FullScreenDialog from "@/modules/common/components/modals/full-screen-modal";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import EditStatusForm from "@/modules/lead-page/components/lead-info/edit-fields/status/edit-status-form";
import { useLeadContext } from "@/modules/lead-page/context/lead-page.context";
import useLeadInfo from "@/modules/lead-page/hooks/lead-info/lead-info.hook";
import Status from "@/modules/common/components/ui-kit/status/status";
import CreateTrackContent from "@/modules/lead-page/new/components/developer-tracks/modals/create-track-content";
import { Box, Typography, useTheme } from "@mui/material";
import { useContext, useState } from "react";
import { userHasPermission } from "@/modules/common/utils";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";

const StatusField = () => {
  const { handleConfirmUpdate, status, isLoading } = useLeadInfo();
  const theme = useTheme();

  const screen = useScreenSize();
  const { lead, isAssignedAgent } = useLeadContext();

  // Two-step flow inside ONE modal: "status" (edit-status form) → "tracks"
  // (create developer tracks). Save & Create Tracks switches the step; plain
  // Save closes as usual. The modal stays open while step === "tracks" even
  // after the status update's success handler flips editLeadStatusModalOpen off.
  const [step, setStep] = useState<"status" | "tracks">("status");
  const flowOpen = status.editLeadStatusModalOpen || step === "tracks";
  const closeFlow = () => {
    setStep("status");
    status.setEditLeadStatusModalOpen(false);
  };
  const modalHeader = step === "tracks" ? "New developer track" : "Edit Status";

  const stepContent = (
    <Box
      key={step}
      sx={{
        width: "100%",
        "@keyframes statusStepSlideIn": {
          from: { opacity: 0, transform: "translateX(24px)" },
          to: { opacity: 1, transform: "translateX(0)" },
        },
        animation: "statusStepSlideIn 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {step === "status"
        ? status.data && (
            <EditStatusForm
              isLoadingSubmition={isLoading}
              handleConfirmUpdate={handleConfirmUpdate}
              status={{ ...status, data: status.data }}
              onOpenCreateTrack={() => setStep("tracks")}
            />
          )
        : <CreateTrackContent onClose={closeFlow} />}
    </Box>
  );

  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const preventEditStatus = userHasPermission(
    { name: PermissionsEnum.PREVENT_EDIT_STATUS, level: PermissionLevel.WRITE },
    currentUserPermissions
  );

  return (
    <>
      <Box
        sx={{
          height: "100%",
          width: {
            mobile: "100%",
            tablet: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",

            justifyContent: screen.isMobile ? "flex-start" : "flex-end",
            gap: theme.nawy.spacing.xxs,
          }}
        >
          <Typography
            sx={{
              fontSize:
                theme.nawy.typography.english.variants.body.xs.regular.fontSize,
              fontWeight:
                theme.nawy.typography.english.variants.body.xs.regular
                  .fontWeight,
              lineHeight:
                theme.nawy.typography.english.variants.body.xs.regular
                  .lineHeight,
            }}
            color={theme.nawy.colors.primitives.grey[700]}
          >
            {lead?.data?.lead?.status?.nextStatusCategory?.name
              ? lead?.data?.lead?.status?.nextStatusCategory?.name
              : lead?.data?.lead?.status?.statusCategory?.name}
          </Typography>
          <Status
            data-testid="lead-status-button"
            label={`${lead?.data?.lead?.status?.status}`}
            bgHexColor={lead?.data?.lead?.status?.color}
            size="lg"
            variant="secondary"
            onClick={() =>
              (!preventEditStatus || isAssignedAgent) &&
              status.setEditLeadStatusModalOpen(true)
            }
          />
        </Box>
      </Box>

      {screen.isMobile ? (
        <FullScreenDialog
          open={flowOpen}
          setOpen={(v: boolean) =>
            v ? status.setEditLeadStatusModalOpen(true) : closeFlow()
          }
          header={modalHeader}
          handleCloseModal={closeFlow}
        >
          {stepContent}
        </FullScreenDialog>
      ) : (
        <EditFieldModal
          header={modalHeader}
          open={flowOpen}
          setOpen={(v: boolean) =>
            v ? status.setEditLeadStatusModalOpen(true) : closeFlow()
          }
          width={700}
          modalFooter={false}
          testId="edit-status-modal"
        >
          {stepContent}
        </EditFieldModal>
      )}
    </>
  );
};

export default StatusField;
